import { useCallback, useEffect, useState } from 'react';

import { pascalCase } from '@/util';

type CreateBreakPointState<T extends Record<string, number>> = {
  [K in keyof T as `is${Capitalize<string & K>}`]: boolean;
};

type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

type UseBreakPointsProps<T extends Record<string, number>> = {
  breakPoint: T;
};

export const useBreakPoints = <T extends Record<string, number>>({
  breakPoint,
}: UseBreakPointsProps<T>): Expand<CreateBreakPointState<T>> => {
  const [breakPoints, setBreakPoints] = useState<CreateBreakPointState<T>>(
    () => {
      const initialState = {} as CreateBreakPointState<T>;

      Object.keys(breakPoint).forEach(key => {
        const isKey = `is${pascalCase(key)}` as keyof CreateBreakPointState<T>;
        (initialState as any)[isKey] = false;
      });

      return initialState;
    },
  );

  const handleChange = useCallback(() => {
    setBreakPoints(() => {
      const mediaResults: Record<string, boolean> = {};

      Object.entries(breakPoint).forEach(([key, value]) => {
        mediaResults[key] = window.matchMedia(
          `(min-width: ${value}px)`,
        ).matches;
      });

      const sortedEntries = Object.entries(breakPoint).sort(
        ([, a], [, b]) => b - a,
      );

      const newState = {} as CreateBreakPointState<T>;

      Object.keys(breakPoint).forEach(key => {
        const isKey = `is${pascalCase(key)}` as keyof CreateBreakPointState<T>;
        (newState as any)[isKey] = false;
      });

      for (const [key] of sortedEntries) {
        if (mediaResults[key]) {
          const isKey =
            `is${pascalCase(key)}` as keyof CreateBreakPointState<T>;
          (newState as any)[isKey] = true;

          break;
        }
      }

      return newState;
    });
  }, [breakPoint]);

  useEffect(() => {
    handleChange();

    window.addEventListener('resize', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
    };
  }, [handleChange]);

  return breakPoints as unknown as Expand<CreateBreakPointState<T>>;
};
